var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { timelinePeriods } from '../data/timeline';
import { ACTIVE_POINT_ANGLE, DESKTOP_BREAKPOINT, MOBILE_BREAKPOINT, getTimelineRadius, polarToCartesian, } from './timeline/config';
import TimelineCircle from './timeline/TimelineCircle';
import TimelineControls from './timeline/TimelineControls';
import TimelineHeader from './timeline/TimelineHeader';
import TimelineMobileFooter from './timeline/TimelineMobileFooter';
import TimelineSlider from './timeline/TimelineSlider';
import TimelineYears from './timeline/TimelineYears';
var HistoricalTimeline = function () {
    var _a = useState(0), activePeriodIndex = _a[0], setActivePeriodIndex = _a[1];
    var _b = useState(true), isSlideBeginning = _b[0], setIsSlideBeginning = _b[1];
    var _c = useState(false), isSlideEnd = _c[0], setIsSlideEnd = _c[1];
    var _d = useState(0), previousPeriodIndex = _d[0], setPreviousPeriodIndex = _d[1];
    var _e = useState(function () {
        if (typeof window === 'undefined') {
            return DESKTOP_BREAKPOINT;
        }
        return window.innerWidth;
    }), viewportWidth = _e[0], setViewportWidth = _e[1];
    var swiperRef = useRef(null);
    var periodsCount = timelinePeriods.length;
    var activePeriod = timelinePeriods[activePeriodIndex];
    var isMobile = viewportWidth < MOBILE_BREAKPOINT;
    useEffect(function () {
        var handleResize = function () {
            setViewportWidth(window.innerWidth);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return function () {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    var radius = useMemo(function () { return getTimelineRadius(viewportWidth); }, [viewportWidth]);
    var points = useMemo(function () {
        var step = 360 / periodsCount;
        return timelinePeriods.map(function (period, index) {
            var angle = ACTIVE_POINT_ANGLE + step * (index - activePeriodIndex);
            var coords = polarToCartesian(0, 0, radius, angle);
            return __assign(__assign({}, period), { angle: angle, x: coords.x, y: coords.y });
        });
    }, [activePeriodIndex, periodsCount, radius]);
    useEffect(function () {
        setIsSlideBeginning(true);
        setIsSlideEnd(activePeriod.events.length <= 1);
        if (swiperRef.current) {
            swiperRef.current.slideTo(0, 0);
            swiperRef.current.update();
        }
    }, [activePeriod]);
    var updatePeriod = useCallback(function (nextIndex) {
        if (nextIndex === activePeriodIndex) {
            return;
        }
        setPreviousPeriodIndex(activePeriodIndex);
        setActivePeriodIndex(nextIndex);
    }, [activePeriodIndex]);
    var handlePeriodClick = useCallback(function (index) {
        updatePeriod(index);
    }, [updatePeriod]);
    var handlePrevPeriod = useCallback(function () {
        updatePeriod(Math.max(0, activePeriodIndex - 1));
    }, [activePeriodIndex, updatePeriod]);
    var handleNextPeriod = useCallback(function () {
        updatePeriod(Math.min(periodsCount - 1, activePeriodIndex + 1));
    }, [activePeriodIndex, periodsCount, updatePeriod]);
    var handlePrevSlide = useCallback(function () {
        if (!swiperRef.current || isSlideBeginning) {
            return;
        }
        swiperRef.current.slidePrev();
    }, [isSlideBeginning]);
    var handleNextSlide = useCallback(function () {
        if (!swiperRef.current || isSlideEnd) {
            return;
        }
        swiperRef.current.slideNext();
    }, [isSlideEnd]);
    var handleSwiperReady = useCallback(function (swiper) {
        swiperRef.current = swiper;
        setIsSlideBeginning(swiper.isBeginning);
        setIsSlideEnd(swiper.isEnd);
    }, []);
    var handleSlideChange = useCallback(function (swiper) {
        setIsSlideBeginning(swiper.isBeginning);
        setIsSlideEnd(swiper.isEnd);
    }, []);
    var canGoPrevPeriod = activePeriodIndex > 0;
    var canGoNextPeriod = activePeriodIndex < periodsCount - 1;
    var canGoPrevSlide = !isSlideBeginning;
    var canGoNextSlide = !isSlideEnd;
    var periodCounterCurrent = String(activePeriodIndex + 1).padStart(2, '0');
    var periodCounterTotal = String(periodsCount).padStart(2, '0');
    var periodDirection = activePeriodIndex >= previousPeriodIndex ? 'forward' : 'backward';
    return (_jsx("section", { className: "timeline", children: _jsxs("div", { className: "timeline__container", children: [_jsx(TimelineHeader, {}), _jsxs("div", { className: "timeline__center", children: [_jsx(TimelineYears, { activePeriodIndex: activePeriodIndex, direction: periodDirection, endYear: activePeriod.endYear, startYear: activePeriod.startYear }), !isMobile && (_jsx(TimelineCircle, { activePeriodIndex: activePeriodIndex, onPeriodClick: handlePeriodClick, points: points })), isMobile && (_jsx("div", { className: "timeline__mobile-label", children: activePeriod.label }))] }), !isMobile && (_jsx(TimelineControls, { canGoNext: canGoNextPeriod, canGoPrev: canGoPrevPeriod, counterCurrent: periodCounterCurrent, counterTotal: periodCounterTotal, onNext: handleNextPeriod, onPrev: handlePrevPeriod })), _jsx(TimelineSlider, { activePeriodId: activePeriod.id, canGoNext: canGoNextSlide, canGoPrev: canGoPrevSlide, events: activePeriod.events, isMobile: isMobile, onNext: handleNextSlide, onPrev: handlePrevSlide, onSlideChange: handleSlideChange, onSwiper: handleSwiperReady }), isMobile && (_jsx(TimelineMobileFooter, { activePeriodIndex: activePeriodIndex, canGoNext: canGoNextPeriod, canGoPrev: canGoPrevPeriod, counterCurrent: periodCounterCurrent, counterTotal: periodCounterTotal, onNext: handleNextPeriod, onPeriodClick: handlePeriodClick, onPrev: handlePrevPeriod, periods: timelinePeriods }))] }) }));
};
export default HistoricalTimeline;
